import { useEffect, useState } from "react";
import { X, Sun, BookOpen, Sparkles, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const KEY = "fs_signup_popup_seen";
const OPTIONS = [
  { id: "daily_verse", icon: Sun, label: "Verse of the Day", hint: "Every morning · verse, deep dive, prompt & prayer" },
  { id: "weekly_devotional", icon: BookOpen, label: "Sunday Devotional", hint: "One honest word to carry into the week" },
  { id: "product_updates", icon: Sparkles, label: "New releases & news", hint: "First look at new ebooks, journals, stickers and sales" },
];

export function SignupPopup() {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [prefs, setPrefs] = useState(OPTIONS.map((o) => o.id));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(KEY) || window.location.pathname.startsWith("/admin") || window.location.pathname === "/success") return;
    let t;
    let cancelled = false;
    api.get("/storefront").then((r) => {
      if (cancelled || !r.data.popup_enabled) return;
      setCfg(r.data);
      t = setTimeout(() => {
        if (!localStorage.getItem(KEY)) setOpen(true);
      }, Math.max(0, (r.data.popup_delay_seconds ?? 4) * 1000));
    }).catch(() => {});
    return () => { cancelled = true; if (t) clearTimeout(t); };
  }, []);

  const close = () => { localStorage.setItem(KEY, "1"); setOpen(false); };
  const toggle = (id) => setPrefs(prefs.includes(id) ? prefs.filter((p) => p !== id) : [...prefs, id]);
  const submit = async (e) => {
    e.preventDefault();
    if (!prefs.length) return toast.error("Pick at least one thing you'd like to receive");
    setBusy(true);
    try {
      await api.post("/subscribe", { email, name, preferences: prefs, source: "popup" });
      toast.success("You're in. Check your inbox for a welcome note.");
      close();
    } catch { toast.error("Could not subscribe — please try again"); } finally { setBusy(false); }
  };

  if (!open || !cfg) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0a192f]/70 backdrop-blur-sm" data-testid="signup-popup" onClick={close}>
      <div className="bg-white max-w-3xl w-full grid md:grid-cols-[5fr_7fr] shadow-2xl animate-[popIn_.35s_ease]" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#0a192f] text-white p-8 lg:p-10 flex flex-col justify-between relative overflow-hidden">
          <div className="absolute -right-10 -bottom-10 w-48 h-48 border-[16px] border-[#d4af37]/20 rounded-full" />
          <div>
            <div className="overline text-[#d4af37]">Faithful Store</div>
            <h2 className="font-serif text-3xl lg:text-4xl mt-4 leading-tight" data-testid="signup-popup-title">{cfg.popup_title}</h2>
            <p className="text-white/75 mt-4 text-sm leading-relaxed">{cfg.popup_text}</p>
          </div>
          <p className="text-xs text-white/50 mt-8">Free. No spam. Unsubscribe anytime.</p>
        </div>
        <form onSubmit={submit} className="p-8 lg:p-10 relative">
          <button type="button" data-testid="signup-popup-close" onClick={close} aria-label="Close" className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center text-[#4a4a4a] hover:bg-[#f4f5f7]"><X className="w-5 h-5" /></button>
          <div className="overline text-[#4a4a4a]">What would you like?</div>
          <div className="mt-3 space-y-2">
            {OPTIONS.map(({ id, icon: Icon, label, hint }) => {
              const on = prefs.includes(id);
              return (
                <button type="button" key={id} data-testid={`signup-pref-${id}`} aria-pressed={on} onClick={() => toggle(id)} className={`w-full flex items-center gap-3 text-left border p-3 transition-colors ${on ? "border-[#d4af37] bg-[#fdfbf7]" : "border-[#e5e5e5] hover:border-[#0a192f]"}`}>
                  <span className={`w-9 h-9 flex items-center justify-center flex-shrink-0 ${on ? "bg-[#0a192f] text-[#d4af37]" : "bg-[#f4f5f7] text-[#4a4a4a]"}`}><Icon className="w-4 h-4" /></span>
                  <span className="flex-1"><span className="block font-semibold text-[#0a192f] text-sm">{label}</span><span className="block text-xs text-[#4a4a4a]">{hint}</span></span>
                  <span className={`w-4 h-4 border flex-shrink-0 ${on ? "bg-[#d4af37] border-[#d4af37]" : "border-[#cfcfcf]"}`} />
                </button>
              );
            })}
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-5">
            <input data-testid="signup-popup-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="First name (optional)" className="border border-[#e5e5e5] px-4 py-3 outline-none focus:border-[#0a192f]" />
            <input data-testid="signup-popup-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="border border-[#e5e5e5] px-4 py-3 outline-none focus:border-[#0a192f]" />
          </div>
          <button data-testid="signup-popup-submit" disabled={busy} className="btn-navy w-full mt-4 inline-flex items-center justify-center gap-2 disabled:opacity-50"><Mail className="w-4 h-4" /> {busy ? "Signing you up…" : "Send me encouragement"}</button>
          <button type="button" data-testid="signup-popup-later" onClick={close} className="w-full mt-3 text-xs text-[#4a4a4a] hover:text-[#0a192f]">Not now, thanks</button>
        </form>
      </div>
      <style>{`@keyframes popIn { from { opacity: 0; transform: translateY(16px) scale(.98) } to { opacity: 1; transform: none } }`}</style>
    </div>
  );
}
